import { Alert, Button, Col, Form, Input, Modal, Row, Select } from "antd";
import { Spinner } from "common/Spinner";
import { debounce } from "lodash";
import { useEffect, useState } from "react";
import {
  useCreateTCUMutation,
  useUpdateTCUMutation,
  useGetNextAvailableTCUTokenQuery,
} from "features/tcu/redux";

export const TCUFormModal = ({
  form,
  title,
  qTCU,
  qCustomers,
  qApplicationUnits,
  isPageLoaded,
  isModalOpen,
  setIsModalOpen,
  refetch,
  isNewForm = false,
  isEditForm = false,
}) => {
  const [isOkButtonLoading, setIsOkButtonLoading] = useState(false);
  const [formErrorMessage, setFormErrorMessage] = useState("");
  const [isShowFormErrorMessage, setIsShowFormErrorMessage] = useState(false);
  const [isGetNextAvailableTCUToken, setIsGetNextAvailableTCUToken] =
    useState(false);

  /**
   * Hooks
   */
  const [createTCU] = useCreateTCUMutation();
  const [updateTCU] = useUpdateTCUMutation();
  const { data: qAvailableTCUToken, refetch: qAvailableTCUTokenRefetch } =
    useGetNextAvailableTCUTokenQuery(undefined, {
      skip: !isGetNextAvailableTCUToken,
    });

  /**
   * Form Values
   */
  const [customersFormSelect, setCustomersFormSelect] = useState([]);
  const [applicationUnitsFormSelect, setApplicationUnitsFormSelect] = useState(
    []
  );

  /**
   * On Finish
   * @param {*} values
   */
  const onFinishFormModal = async (values) => {
    const {
      formName,
      formUnitNumber,
      formIMEI,
      formSIM,
      formToken,
      formCustomersSelect,
      formApplicationUnitsSelect,
    } = values;
    setIsOkButtonLoading(true);
    try {
      let result = { error: { data: { message: "Empty Form" } } };

      const selectedCustomer =
        qCustomers.find((item) => item.id === formCustomersSelect)?.id || null;

      let selectedApplicationUnit =
        qApplicationUnits.find((item) => item.id === formApplicationUnitsSelect)
          ?.id || null;

      if (isNewForm && !isEditForm) {
        result = await createTCU({
          fields: {
            name: formName,
            unit_number: formUnitNumber,
            imei: formIMEI,
            sim: formSIM,
            token: formToken,
            product_sn_id: selectedApplicationUnit,
            customer_id: selectedCustomer,
          },
        });
      } else if (isEditForm) {
        if (
          selectedApplicationUnit == null &&
          formApplicationUnitsSelect === qTCU.product_sn.id
        ) {
          selectedApplicationUnit = qTCU.product_sn.id;
        }
        result = await updateTCU({
          id: qTCU?.id,
          name: formName,
          unit_number: formUnitNumber,
          imei: formIMEI,
          sim: formSIM,
          token: formToken,
          product_sn_id: selectedApplicationUnit,
          customer_id: selectedCustomer,
        });
      }
      if ("error" in result) {
        setIsOkButtonLoading(false);
        setFormErrorMessage(result.error.data.message);
        setIsShowFormErrorMessage(true);
        return;
      }
      refetch();
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error: ", error);
    }
  };

  useEffect(() => {
    if (isPageLoaded) {
      // initial default options
      let customersSelectOptions = [{ label: "None", value: 0 }];
      let applicationUnitsSelectOptions = [{ label: "None", value: 0 }];

      if (qTCU?.product_sn) {
        applicationUnitsSelectOptions.push({
          label: qTCU.product_sn.product_sn_name,
          value: qTCU.product_sn.id,
        });
      }

      // loaded options
      const loadedCustomersSelectOptions = qCustomers?.map((obj) => {
        return {
          value: obj.id,
          label: obj.name,
        };
      });
      const loadedApplicationUnitsSelectOptions = qApplicationUnits?.map(
        (obj) => {
          return {
            value: obj.id,
            label: obj.product_sn_name,
          };
        }
      );

      // for new forms, enable fetch for auto-generated tokens
      if (isNewForm) {
        setIsGetNextAvailableTCUToken(true);
      }

      // concat default + loaded options
      customersSelectOptions = customersSelectOptions.concat(
        loadedCustomersSelectOptions
      );
      applicationUnitsSelectOptions = applicationUnitsSelectOptions.concat(
        loadedApplicationUnitsSelectOptions
      );
      setCustomersFormSelect(customersSelectOptions);
      setApplicationUnitsFormSelect(applicationUnitsSelectOptions);
    }
  }, [isPageLoaded, qTCU, qCustomers, qApplicationUnits]);

  useEffect(() => {
    if (isModalOpen) {
      form.resetFields();
      if (qAvailableTCUToken != null) {
        qAvailableTCUTokenRefetch();
      }
    }
    setFormErrorMessage("");
    setIsShowFormErrorMessage(false);
    setIsOkButtonLoading(false);
  }, [isModalOpen, form]);

  useEffect(() => {
    if (qAvailableTCUToken && isModalOpen) {
      const { token } = qAvailableTCUToken;
      form.setFieldsValue({ formToken: token });
    }
  }, [isModalOpen, qAvailableTCUToken, form]);

  if (!isPageLoaded) return <Spinner />;

  const handleAutoGenerateToken = async () => {
    if (qAvailableTCUToken == null) return;
    if (qTCU?.token != null && qTCU.token !== "") {
      console.error("handleAutoGenerateToken() -- token already exists");
      return;
    }
    qAvailableTCUTokenRefetch();
  };

  /**
   * Show Form Error Message
   * @returns
   */
  const showFormErrorMessage = () => {
    if (isShowFormErrorMessage) {
      return (
        <>
          <Alert
            message="Error"
            description={formErrorMessage}
            type="error"
            showIcon
          />
          <br />
        </>
      );
    }
    return <></>;
  };

  return (
    <>
      <Modal
        title={title}
        width={1000}
        open={isModalOpen}
        okText={isNewForm ? "Create" : "Save"}
        okButtonProps={{ autoFocus: true, htmlType: "submit" }}
        cancelButtonProps={{ disabled: isOkButtonLoading }}
        confirmLoading={isOkButtonLoading}
        destroyOnClose
        forceRender
        onCancel={debounce(async () => {
          setIsModalOpen(false);
        }, 100)}
        modalRender={(dom) => (
          <Form
            layout="vertical"
            form={form}
            name="form-tcu-modal"
            onFinish={async (values) => onFinishFormModal(values)}
          >
            {dom}
          </Form>
        )}
      >
        {showFormErrorMessage()}
        <Row>
          <Col span={10} offset={1}>
            <Form.Item
              label="TCU Name"
              name="formName"
              initialValue={qTCU?.name}
              rules={[
                {
                  required: true,
                  message: "Missing TCU Name!",
                },
              ]}
            >
              <Input disabled={isOkButtonLoading} />
            </Form.Item>
          </Col>
          <Col span={10} offset={2}>
            <Form.Item
              label="Unit Number"
              name="formUnitNumber"
              initialValue={qTCU?.unit_number}
            >
              <Input disabled={isOkButtonLoading} />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={10} offset={1}>
            <Form.Item label="IMEI" name="formIMEI" initialValue={qTCU?.imei}>
              <Input disabled={isOkButtonLoading} />
            </Form.Item>
          </Col>
          <Col span={10} offset={2}>
            <Form.Item label="SIM" name="formSIM" initialValue={qTCU?.sim}>
              <Input disabled={isOkButtonLoading} />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={10} offset={1}>
            <Form.Item
              label="Token"
              name="formToken"
              initialValue={qTCU?.token}
              rules={[
                {
                  required: true,
                  message: "Missing TCU Token!",
                },
              ]}
            >
              <Input disabled={isOkButtonLoading} />
            </Form.Item>
          </Col>
          {isNewForm ? (
            <>
              <Col span={10} offset={2}>
                <Button
                  style={{ marginTop: "20px" }}
                  type="primary"
                  loading={isOkButtonLoading}
                  onClick={handleAutoGenerateToken}
                >
                  Auto Generate Token
                </Button>
              </Col>
            </>
          ) : (
            <></>
          )}
        </Row>
        <Row>
          <Col span={10} offset={1}>
            <Form.Item
              label="Customer Associated"
              name="formCustomersSelect"
              initialValue={qTCU?.customer_id || 0}
            >
              <Select
                disabled={isOkButtonLoading}
                options={customersFormSelect}
              />
            </Form.Item>
          </Col>
          <Col span={10} offset={2}>
            <Form.Item
              label="Application Unit Associated"
              name="formApplicationUnitsSelect"
              initialValue={qTCU?.product_sn_id || 0}
            >
              <Select
                disabled={isOkButtonLoading}
                options={applicationUnitsFormSelect}
              />
            </Form.Item>
          </Col>
        </Row>
      </Modal>
    </>
  );
};
